import { gql } from '@apollo/client';

/** Per-user sold lines via tickets.u_id, including manual commission overrides. */
const USER_TICKET_STATS_FIELDS = `
  tickets(where: { t_deleted_at: { _is_null: true } }) {
    soldTickets: ticket_placements(
      where: {
        tp_status: { _in: [Sold, PaidManuallyViaQA] }
        tp_deleted_at: { _is_null: true }
        tp_is_support_vanderbilt_nil_fund: { _eq: false }
      }
    ) {
      tp_quick_cut_amount
      tp_sales_rep_commission_override
    }
    listedCount: ticket_placements_aggregate(
      where: {
        tp_status: { _eq: List }
        tp_is_published: { _eq: true }
        tp_deleted_at: { _is_null: true }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const GET_SALES_REPS = gql`
  query GetSalesReps {
    sales_reps {
      sr_commission_rate
      sr_email
      sr_first_name
      sr_last_name
      sr_phone
      sr_created_at
      sr_deleted_at
      sr_updated_at
      sr_id
      sr_total_paid_out
    }
  }
`;

export const GET_SALES_REPS_BY_EMAIL = gql`
  query GetSalesRepsByEmail($email: String!) {
    sales_reps(
      where: {
        sr_email: { _eq: $email }
      }
    ) {
      sr_commission_rate
      sr_email
      sr_first_name
      sr_last_name
      sr_phone
      sr_created_at
      sr_deleted_at
      sr_updated_at
      sr_id
      sr_total_paid_out
    }
  }
`;

export const GET_MANAGED_USERS = gql`
  query GetManagedUsers {
    managedUsers: user(
      where: {
        u_delete_requested: { _eq: false }
        u_sales_reps_id: { _is_null: false }
      }
    ) {
      u_id
      u_full_name
      u_email_id
      u_sales_reps_id
      tickets_sold_amount
      u_is_active
      ${USER_TICKET_STATS_FIELDS}
    }
  }
`;

export const GET_MANAGED_USERS_BY_SALES_REP = gql`
  query GetManagedUsersBySalesRep($salesRepId: uuid!) {
    managedUsers: user(
      where: {
        u_delete_requested: { _eq: false }
        u_sales_reps_id: { _eq: $salesRepId }
      }
    ) {
      u_id
      u_full_name
      u_email_id
      u_sales_reps_id
      tickets_sold_amount
      u_is_active
      ${USER_TICKET_STATS_FIELDS}
    }
  }
`;

export const GET_MANAGED_USERS_COUNT = gql`
  query GetManagedUsersCount {
    managedUsersCount: user_aggregate(
      where: {
        u_delete_requested: { _eq: false }
        u_sales_reps_id: { _is_null: false }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const GET_MANAGED_USERS_COUNT_BY_SALES_REP = gql`
  query GetManagedUsersCountBySalesRep($salesRepId: uuid!) {
    managedUsersCount: user_aggregate(
      where: {
        u_delete_requested: { _eq: false }
        u_sales_reps_id: { _eq: $salesRepId }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const GET_SALES_REP_USERS = gql`
  query GetSalesRepUsers(
    $salesRepId: uuid!
    $limit: Int = 20
    $offset: Int = 0
    $searchText: String = "%%"
  ) {
    user(
      where: {
        u_sales_reps_id: { _eq: $salesRepId }
        u_delete_requested: { _eq: false }
        _or: [
          { u_full_name: { _ilike: $searchText } }
          { u_email_id: { _ilike: $searchText } }
        ]
      }
      limit: $limit
      offset: $offset
      order_by: { u_created_at: desc }
    ) {
      u_id
      u_full_name
      u_first_name
      u_last_name
      u_email_id
      u_phone_number
      u_is_active
      u_created_at
      tickets_sold_amount
      ${USER_TICKET_STATS_FIELDS}
    }
    user_aggregate(
      where: {
        u_sales_reps_id: { _eq: $salesRepId }
        u_delete_requested: { _eq: false }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const GET_SALES_REP_BY_EMAIL = gql`
  query GetSalesRepByEmail($email: String!) {
    sales_reps(
      where: {
        sr_email: { _eq: $email }
      }
    ) {
      sr_id
      sr_email
      sr_first_name
      sr_last_name
      sr_commission_rate
    }
  }
`;

// Creates a Cognito account for the sales rep — password stored ONLY in Cognito, never in the DB
export const CREATE_SALES_REP_COGNITO_USER = gql`
  mutation CreateMarketingUser($createMarketingUserInput: createMarketingUserInput!) {
    createMarketingUser(createMarketingUserInput: $createMarketingUserInput) {
      message
    }
  }
`;

export const ADD_SALES_REP = gql`
  mutation AddSalesRep($object: sales_reps_insert_input!) {
    insert_sales_reps_one(object: $object) {
      sr_id
      sr_first_name
      sr_last_name
      sr_email
      sr_phone
      sr_commission_rate
      sr_created_at
      sr_updated_at
    }
  }
`;

export const UPDATE_SALES_REP_BY_ID = gql`
  mutation UpdateSalesRepById($salesRepId: uuid!, $salesRepInput: sales_reps_set_input!) {
    update_sales_reps_by_pk(
      pk_columns: { sr_id: $salesRepId }
      _set: $salesRepInput
    ) {
      sr_id
      sr_first_name
      sr_last_name
      sr_email
      sr_phone
      sr_commission_rate
      sr_updated_at
    }
  }
`;

export const UPDATE_SALES_REP_TOTAL_PAID_OUT = gql`
  mutation UpdateSalesRepTotalPaidOut($salesRepId: uuid!, $totalPaidOut: numeric!) {
    update_sales_reps_by_pk(
      pk_columns: { sr_id: $salesRepId }
      _set: { sr_total_paid_out: $totalPaidOut }
    ) {
      sr_id
      sr_total_paid_out
      sr_updated_at
    }
  }
`;

export const INCREMENT_SALES_REP_TOTAL_PAID_OUT = gql`
  mutation IncrementSalesRepTotalPaidOut($salesRepId: uuid!, $amount: numeric!) {
    update_sales_reps_by_pk(
      pk_columns: { sr_id: $salesRepId }
      _inc: { sr_total_paid_out: $amount }
    ) {
      sr_id
      sr_total_paid_out
      sr_updated_at
    }
  }
`;

export const UNASSIGN_USERS_FROM_SALES_REP = gql`
  mutation UnassignUsersFromSalesRep($salesRepId: uuid!) {
    update_user(
      where: { u_sales_reps_id: { _eq: $salesRepId } }
      _set: { u_sales_reps_id: null }
    ) {
      affected_rows
      returning {
        u_id
        u_full_name
      }
    }
  }
`;

export const DEACTIVATE_SALES_REP = gql`
  mutation DeactivateSalesRep($salesRepId: uuid!, $deletedAt: timestamptz!) {
    update_sales_reps_by_pk(
      pk_columns: { sr_id: $salesRepId }
      _set: { sr_deleted_at: $deletedAt }
    ) {
      sr_id
      sr_first_name
      sr_last_name
      sr_deleted_at
    }
  }
`;